import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { useState, useEffect, useMemo } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import * as Yup from 'yup';
import {
  GridLegacy,
  Typography,
  Stack,
  FormControl,
  FormHelperText,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Formik } from 'formik';
import cofactrLogo from 'assets/images/cofactr_logo.png';
import { useSelector } from 'store';
import {
  useUpdateOrgMutation,
  useGetOrgQuery,
  useUploadOrgLogoMutation,
} from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { handleErr } from 'utils/functions';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { gridSpacing } from 'constants/themeConstants';
import { API_URL, IS_LOCAL } from 'constants/envConstants';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  formConstants,
  formValidation,
} from 'views/org/SubOrgs/components/constants';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { HookFormInput, HookFormToggle } from 'ui-component/HookFormComponents';
import useSnackbar from 'hooks/useSnackbar';
import DetailCustomProperties from 'ui-component/DetailCustomProperties';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import useGetCustomPropertyDefinitions from 'hooks/useGetCustomPropertyDefinitions';
import { Org } from 'types/org';
import { useIsCofactr } from 'hooks/useIsCofactr';
import OnboardingAlert from 'views/org/OnboardingAlert';

const OrgInfo = () => {
  const { isCofactr } = useIsCofactr();
  const theme = useTheme();
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  // Org fetching handled in OrgLayout, parent to this component as per MainRoutes
  const { activeOrgId } = useSelector((state) => state.org);
  const {
    data: org,
    isSuccess: isFetchOrgSuccess,
    isLoading: isLoadingOrg,
  } = useGetOrgQuery(activeOrgId ?? skipToken);
  const [uploadOrgLogo, { isLoading: isUploadingLogo }] =
    useUploadOrgLogoMutation();

  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();

  const { customProperties } = useGetCustomPropertyDefinitions({
    model: CustomPropertyModel.ORG,
    visibilityContext: CustomPropertyVisibility.INFO_SECTION,
  });

  const defaultValues = {
    [formConstants.name.name]: org?.name ?? '',
    [formConstants.website.name]: org?.website ?? '',
    [formConstants.industry.name]: org?.industry ?? '',
    [formConstants.militaryUse.name]: org?.militaryUse ?? false,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (org) {
      reset(defaultValues);
    }
  }, [org]);

  const onSubmit = async (payload: FieldValues) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar('Preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  const orgLogo = useMemo(() => {
    // only set the image if there is one
    // image url in production is full url but in development is missing the host
    // images that start with http were generated in fixtures and should be unchanged
    if (org && org.image?.startsWith('http')) {
      return org.image;
    }
    if (org && org.image && org.image.length > 0) {
      return `${IS_LOCAL ? API_URL : ''}${org.image}`;
    }
    return null;
  }, [org]);

  return (
    <GridLegacy container spacing={gridSpacing}>
      {org?.onboardingRequired && isCofactr && (
        <GridLegacy item xs={12}>
          <OnboardingAlert />
        </GridLegacy>
      )}
      <GridLegacy item xs={6}>
        <Stack spacing={gridSpacing}>
          <MainCard title="Logo">
            {org?.name ? (
              <Stack sx={{ alignItems: 'center' }} spacing={gridSpacing}>
                <img src={orgLogo || cofactrLogo} alt="Logo" width="100" />
                <Typography variant="subtitle2">
                  Upload / Change Your Logo
                </Typography>
                {isFetchOrgSuccess ? (
                  <Formik
                    initialValues={{
                      file: [],
                      submit: null,
                    }}
                    validationSchema={Yup.object({
                      file: Yup.array()
                        .min(1, 'Select a logo to upload')
                        .max(1, 'You may only upload one image'),
                    })}
                    onSubmit={async (values, { setErrors, setStatus }) => {
                      try {
                        setStatus({ success: false });
                        const data = new FormData();
                        data.append('image', values.file[0]);

                        await uploadOrgLogo({
                          orgId: org!.id,
                          payload: data,
                        }).unwrap();

                        setStatus({
                          success: 'Successfully Uploaded!',
                        });
                      } catch (err) {
                        handleErr(err, (errMessage) =>
                          setErrors({ submit: errMessage })
                        );
                      }
                    }}
                  >
                    {({
                      errors: imgFormErrors,
                      handleBlur,
                      handleSubmit: imgFormHandleSubmit,
                      setFieldValue,
                      touched,
                      values,
                      status,
                    }) => (
                      <>
                        <form noValidate onSubmit={imgFormHandleSubmit}>
                          <FormControl
                            fullWidth
                            error={Boolean(touched.file && errors.file)}
                            sx={{
                              ...theme.typography.customInput,
                            }}
                          >
                            <input
                              id="outlined-adornment-file"
                              type="file"
                              name="file"
                              accept="image/*"
                              onBlur={handleBlur}
                              onChange={(event) => {
                                setFieldValue(
                                  'file',
                                  Array.from(event.currentTarget.files || [])
                                );
                              }}
                            />
                            {touched.file && imgFormErrors.file && (
                              <FormHelperText
                                error
                                id="standard-weight-helper-text-name"
                              >
                                {imgFormErrors.file}
                              </FormHelperText>
                            )}
                          </FormControl>

                          {imgFormErrors.submit && (
                            <Box sx={{ mt: 3 }}>
                              <FormHelperText error>
                                {imgFormErrors.submit}
                              </FormHelperText>
                            </Box>
                          )}
                          {status?.success && (
                            <Box sx={{ mt: 3 }}>
                              <FormHelperText
                                sx={{
                                  color: 'grey.900',
                                }}
                              >
                                Successfully updated!
                              </FormHelperText>
                            </Box>
                          )}
                          <SegmentLoadingButton
                            loading={isUploadingLogo}
                            variant="contained"
                            size="small"
                            disabled={!values.file.length}
                            type="submit"
                            permissionScope={{
                              app: ALL_APPS.ORG_SETTINGS.id,
                              accessLevel: appPermissionAccessLevels.edit,
                            }}
                          >
                            Upload Logo
                          </SegmentLoadingButton>
                        </form>
                      </>
                    )}
                  </Formik>
                ) : (
                  <div>Loading...</div>
                )}
              </Stack>
            ) : (
              <div>Loading...</div>
            )}
          </MainCard>
          {Boolean((customProperties ?? []).length) && (
            <MainCard title="Custom Properties">
              {org && (
                <DetailCustomProperties<Org>
                  useMutation={useUpdateOrgMutation}
                  record={org}
                  model={CustomPropertyModel.ORG}
                  isLoadingRecord={isLoadingOrg}
                  idProp="orgId"
                  xs={6}
                  otherProps={{
                    permissionScope: {
                      app: ALL_APPS.ORG_SETTINGS.id,
                      accessLevel: appPermissionAccessLevels.edit,
                    },
                  }}
                  submitAsClientV2Api={false}
                />
              )}
            </MainCard>
          )}
        </Stack>
      </GridLegacy>
      <GridLegacy item xs={6}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardSectionWithAction
            contents={
              <FormControl fullWidth sx={{ mt: 2 }}>
                <HookFormInput
                  errors={errors}
                  control={control}
                  name={formConstants.name.name}
                  label={formConstants.name.label}
                  required
                  disabled={!isEditing}
                />
                <HookFormInput
                  errors={errors}
                  control={control}
                  name={formConstants.website.name}
                  label={formConstants.website.label}
                  disabled={!isEditing}
                />
                <HookFormInput
                  errors={errors}
                  control={control}
                  name={formConstants.industry.name}
                  label={formConstants.industry.label}
                  required
                  disabled={!isEditing}
                />
                <HookFormToggle
                  errors={errors}
                  control={control}
                  name={formConstants.militaryUse.name}
                  label={formConstants.militaryUse.label}
                  disabled={!isEditing}
                />
                <Typography variant="subtitle2" sx={{ my: 2 }}>
                  We collect the industry associated with your end-use to ensure
                  compliance with supplier restrictions and to help prevent
                  delays in processing your orders.
                </Typography>
                <HookFormInput
                  errors={errors}
                  control={control}
                  name={formConstants.dpas.name}
                  label={formConstants.dpas.label}
                  disabled={!isEditing}
                />
              </FormControl>
            }
            action={
              isEditing ? (
                <SegmentLoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="medium"
                  loading={isUpdatingOrg}
                  permissionScope={{
                    app: ALL_APPS.ORG_SETTINGS.id,
                    accessLevel: appPermissionAccessLevels.edit,
                  }}
                >
                  {isDirty ? 'Save' : 'Cancel'}
                </SegmentLoadingButton>
              ) : (
                <SegmentButton
                  type="button"
                  variant="contained"
                  onClick={() => setIsEditing(true)}
                  sx={{ float: 'right' }}
                  permissionScope={{
                    app: ALL_APPS.ORG_SETTINGS.id,
                    accessLevel: appPermissionAccessLevels.edit,
                  }}
                >
                  Edit
                </SegmentButton>
              )
            }
            title="Billing Contact"
          />
        </form>
      </GridLegacy>
    </GridLegacy>
  );
};

export default OrgInfo;
